// ** React Imports
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

// ** MUI Imports
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  IconButton,
  FormControl,
  FormLabel,
  FormControlLabel,
  Checkbox,
  MenuItem,
  FormHelperText,
  Tooltip 
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

// ** Custom Component Imports
import CustomTextField from "components/mui/text-field";
import Icon from "components/icon";
import CustomAutocomplete from "components/mui/autocomplete";
import InputAdornment from "@mui/material/InputAdornment";
import AddIcon from "@mui/icons-material/Add";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

// ** Third-Party Imports
import { toast } from "react-toastify";

// ** Logic Imports
import { useServicePermission } from "../permissions/ServicePermissions";
import { useServiceLogic } from "../logic/serviceLogic";
import { useIndexLogic } from "../logic/index-logic";

// ** Dialog Imports
import CreateServiceDialog from "./CreateService";

const AddBundle = ({ open, handleClose, bundleContent, BundleProps }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  
  //#region Permissions
  const { ServicePermissionProps } = useServicePermission();
  const {
    hasBundleSavePermission,
    hasServiceSavePermission,
  } = ServicePermissionProps;
  //#endregion Permissions

  const {
    bundle,
    bundleErrors,
    handleBundleChange,
    handleServiceChange,
    addService,
    removeService,
    validateFields,
    ResetBundle,
    handleSaveBundle,
    setBundle,  
    // serviceList,      
  } = BundleProps;
  const { IndexProps } = useIndexLogic({ toast });

const { serviceList, fetchServices } = IndexProps;


  //#region Service Dialog States and Logic
  const [openServiceDlg, setOpenServiceDlg] = useState(false);
  const [serviceContent, setServiceContent] = useState({
    title: 'Create New Service',
    btnText: 'Save Service',
    notification: 'Service saved successfully.'
  });

  const handleOpenServiceDlg = () => {
    setServiceContent({
      title: 'Create New Service',
      btnText: 'Save Service',
      notification: 'Service saved successfully.'
    });
    setOpenServiceDlg(true);
  };

    const handleCloseServiceDlg = async () => {
    setOpenServiceDlg(false);
    await fetchServices(true); 
  };


  // Initialize service logic
  const { ServiceProps } = useServiceLogic({
    setLoading,
    toast,
    navigate,
    handleCloseServiceDlg,
    serviceContent,
    fetchServices: fetchServices || (() => {}),
  });
  //#endregion Service Dialog

  const handleCloseControl = () => {
    ResetBundle();
    handleClose();
  };

  // Augment service list with "Create New Service" option at the top
  const augmentedServiceOptions = [
    { id: -1, serviceOfferingName: "+ Create New Service", industryName: "" },
    ...serviceList.filter((x) => x.isActive === true)
  ];

  const formatNumberWithCommas = (value) => {
  if (value === null || value === undefined) return '';

  const str = value.toString();

  // Allow partial numbers while typing (e.g. ".", "0.", ".5")
  if (str === '' || str === '.' || str === '0.' || /^\.\d*$/.test(str)) {
    return str;
  }

  // Remove commas and format integer part
  const parts = str.replace(/,/g, '').split('.');
  if (parts[0] !== '') {
    const num = Number(parts[0]);
    // Check if it's actually a valid number before formatting
    if (!isNaN(num)) {
      parts[0] = num.toLocaleString('en-US');
    }
  }

  return parts.join('.');
};



const removeCommas = (value) => value.replace(/,/g, '');


  return (
    <>
      <Dialog
        fullWidth={true}
        maxWidth="sm"
        open={open}
        onClose={handleCloseControl}
        aria-labelledby="form-dialog-title"
      >
        <DialogTitle
          sx={{
            borderBottom: "1px solid #D9D9D9",
            paddingBottom: "20px !important",
          }}
        >
          <Box>
            <Typography variant="h4" fontWeight="bold">
              {bundleContent.title}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <CustomTextField
                required
                fullWidth
                label="Bundle Name"
                placeholder="Enter your desired bundle name"
                value={bundle?.bundleName || ""}
                onChange={(e) =>
                  handleBundleChange("bundleName", e.target.value)
                }
                error={!!bundleErrors?.bundleName}
                helperText={bundleErrors?.bundleName || ""}
              />
            </Grid>
            <Grid item xs={12}>
              <CustomTextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                placeholder="Add a description..."
                value={bundle.description || ""}
                onChange={(e) =>
                  handleBundleChange("description", e.target.value)
                }
              />
            </Grid>
            {bundle.services.map((service, index) => (
              <Grid item xs={12} key={index} sx={{ mb: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", md: "row" },
                    alignItems: "flex-start", 
                    gap: 2,
                    width: "100%",
                  }}
                >
                  {/* Service Dropdown - 50% width */}
                  <Box
                    sx={{
                      width: { xs: "100%", md: "50%" }, 
                      minWidth: { xs: "160px", md: "auto" }, 
                      mt:1
                    }}
                  >
                 <CustomAutocomplete
                    fullWidth
                    required
                    value={
                      augmentedServiceOptions.find(
                        (serviceItem) =>
                          serviceItem.id === service?.serviceOfferingID &&
                          serviceItem.id !== -1
                      ) || null
                    }
                    onChange={(e, newValue) => {
                      if (newValue && newValue.id === -1) {
                        handleOpenServiceDlg();
                        return;
                      }
                      handleServiceChange(
                        index,
                        "serviceOfferingID",
                        newValue ? newValue.id : null
                      );
                    }}
                    options={augmentedServiceOptions.sort((a, b) => {
                      if (a.id === -1) return -1;
                      if (b.id === -1) return 1;
                      const optionA = a.industryName + " - " + a.serviceOfferingName;
                      const optionB = b.industryName + " - " + b.serviceOfferingName;
                      return optionA.localeCompare(optionB);
                    })}
                    getOptionLabel={(option) => {
                      if (option?.id === -1) return option?.serviceOfferingName || "";
                      return option?.industryName +
                        " - " +
                        option?.serviceOfferingName || "";
                    }}
                    isOptionEqualToValue={(option, value) =>
                      option?.id === value?.id
                    }
                    renderInput={(params) => (
                      <CustomTextField
                        {...params}
                        placeholder="Select Service"
                        label="Service"
                        required
                        error={
                          !!bundleErrors[`service_${index}_serviceOfferingID`]
                        }
                        helperText={
                          bundleErrors[`service_${index}_serviceOfferingID`]
                        }
                        FormHelperTextProps={{
                          sx: { marginTop: 0.5, height: "20px", mt: 1 },
                        }}
                      />
                    )}
                    renderOption={(props, serviceItem) => {
                      if (serviceItem.id === -1) {
                        return (
                          <li {...props} key="add-service">
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Typography
                                sx={{
                                  fontWeight: 'bold',
                                  color: hasServiceSavePermission ? 'primary.main' : 'text.disabled',
                                }}
                              >
                                + Create New Service
                              </Typography>
                            </Box>
                          </li>
                        );
                      }
                      return (
                        <li {...props} key={serviceItem.id}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                            }}
                          >
                            <Typography variant="body1">
                              {serviceItem.industryName +
                                " - " +
                                serviceItem.serviceOfferingName}
                            </Typography>
                          </Box>
                        </li>
                      );
                    }}
                  />
                  </Box>
                 <Box
                   sx={{
                     display: "flex",
                     flexDirection: { xs: "row", md: "row" },
                     alignItems: "flex-start",
                     gap: 3,
                     width: { xs: "100%", md: "38%" },
                   }}
                >
                  <Box
                    sx={{
                      width: { xs: "60%", md: "60%" },
                      minWidth: { xs: "75px", md: "auto" },
                    }}
                   >
                   <CustomTextField
                      fullWidth
                      label={                
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                              <Tooltip 
                      title={
                      <Box>
                        <Typography variant="body2" sx={{ color: "white" }}>
                        This amount will only be used by default if you charge a Flat Rate for this Service. It can always be changed at the time of Estimate or Work Order. If you charge for Time and Materials, this Flat Rate will not be used.
                        </Typography>
                        </Box>
                      }
                        arrow
                        placement="top"
                        componentsProps={{
                          tooltip: {
                            sx: {
                              transform: 'translateX(10px) !important', 
                            }
                          }
                        }}
                      >
                          <IconButton size="small" sx={{ p: 0, color: "text.secondary" }}>
                            <InfoOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        Flat Rate
                      
                      </Box>
                      }
                      placeholder="0.00"
                      value={formatNumberWithCommas(service.flatRate || "")}
                      onChange={(e) =>
                        handleServiceChange(index, "flatRate", removeCommas(e.target.value))
                      }
                      // error={!!bundleErrors[`service_${index}_flatRate`]}
                      // helperText={bundleErrors[`service_${index}_flatRate`]}
                      // FormHelperTextProps={{
                      //   sx: { marginTop: 0.5, height: "20px" },
                      // }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <span style={{ fontSize: "1.2rem" }}>$</span>
                          </InputAdornment>
                        ),
                      }}
                      inputProps={{ inputMode: "numeric" }}
                    />
                  </Box>

                   <Box
                     sx={{
                       width: { xs: "30%", md: "8%" },
                       display: "flex",
                       alignItems: "center",
                       height: "56px",
                       justifyContent: { xs: "flex-start", md: "center" },
                     }}
                    >

    
    
                    {index !== 0 && (
                      <IconButton
                        onClick={() => removeService(index)}
                        color="error"
                        sx={{ alignSelf: "flex-end" }}
                      >
                        <Icon icon="tabler:trash" />
                      </IconButton>
                    )}
                  </Box>
                  </Box>
                </Box>
              </Grid>
            ))}
            {bundleErrors.services && (
              <FormHelperText
                sx={{
                  color: "error.main",
                  marginTop: 0,
                  fontSize: "13px",
                  ml: 5,
                }}
              >
                {bundleErrors.services}
              </FormHelperText>
            )}
            <Grid item xs={12} md={5}>
              <Box sx={{ display: "flex", alignItems: "flex-end" }}>
                <Box>
                  <Button
                    variant="outlined"
                    color="main"
                    startIcon={<AddIcon />}
                    onClick={addService}
                    disabled={
                      !bundle.services[bundle.services.length - 1]
                        ?.serviceOfferingID ||
                      !bundle.services[bundle.services.length - 1]?.flatRate
                    }
                  >
                    Add Service
                  </Button>
                </Box>
              </Box>
            </Grid>

            {/* ✅ Updated "Active" with Info Icon */}
            <Grid item xs={12}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {/* <FormControlLabel
                  control={
                    <Checkbox
                      color="success"
                      checked={bundle.isActive ?? false}
                      onChange={(e) => {
                        handleBundleChange("isActive", e.target.checked);
                      }}
                    />
                  }
                  label="Active"

                /> */}
                <Box>
                   <Tooltip
                  arrow
                  title={
                    <Box>
                      <Typography variant="body2" sx={{ color: "white" }}>
                        Toggle between Active and Inactive states for this
                        bundle. Active means it’s currently available for use,
                        while Inactive disables it until reactivated.
                      </Typography>
                    </Box>
                  }
                    placement="right"
                    componentsProps={{
                      tooltip: {
                        sx: {
                          transform: 'translateX(10px) !important', 
                        }
                      }
                    }}
                >
                  <IconButton size="small" color="info">
                    <Icon icon="tabler:info-circle" style={{ fontSize: 22 }} />
                  </IconButton>
                </Tooltip>
                 <FormLabel sx={{ mr: 2 }}>
                    {bundle.isActive ? "Active" : "Inactive"}
                  </FormLabel>
                  <label class="switch">
                    <input
                      type="checkbox"
                      checked={bundle.isActive ?? false}
                      onChange={(e) => {
                        handleBundleChange("isActive", e.target.checked);
                      }}
                    />
                    <span class="slider round"></span>
                  </label>
                 
                </Box>
               
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            padding: "20px 24px !important",
            boxShadow: `0px -4px 8px rgb(0, 0, 0, 0.1)`,
            zIndex: 1,
            gap: 2, // Add gap between stacked items
          }}
          className="dialog-actions-dense"
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" }, // Stack buttons on mobile
              gap: 2,
              width: { xs: "100%", sm: "auto" }, // Full width on mobile
              alignItems: { xs: "stretch", sm: "center" }, // Stretch buttons on mobile
            }}
          >
            <Button
              variant="contained"
              color="info"
              onClick={handleCloseControl}
            >
              Cancel
            </Button>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" }, // Stack buttons on mobile
              gap: 2,
              width: { xs: "100%", sm: "auto" }, // Full width on mobile
              alignItems: { xs: "stretch", sm: "center" }, // Stretch buttons on mobile
            }}
          >
            <Button variant="tonal" color="info" onClick={ResetBundle}>
              Reset
            </Button>
            <Button
              variant="contained"
              color="main"
              disabled={!hasBundleSavePermission}
              {...(hasBundleSavePermission && {
                onClick: () => handleSaveBundle(),
              })}
            >
              {bundleContent.btnText}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Create Service Dialog */}
      <CreateServiceDialog
        open={openServiceDlg}
        handleClose={handleCloseServiceDlg}
        serviceContent={serviceContent}
        ServiceProps={ServiceProps}
      />
    </>
  );
};

export default AddBundle;
